import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraProgramSidebar, SpecraSidebarBackLink } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { SidebarUserMenu } from '../SidebarUserMenu';
export function recordNavHref(basePath, def) {
    if (def.href !== undefined) return def.href;
    if (def.path === undefined) return undefined;
    return def.path === '' ? basePath : `${basePath}/${def.path}`;
}
export function isRecordNavActive(pathname, basePath, def) {
    const href = recordNavHref(basePath, def);
    if (!href || def.onClick) return false;
    const exact = def.exact ?? def.path === '';
    return exact ? pathname === href || pathname === `${href}/` : pathname === href || pathname.startsWith(`${href}/`);
}
/**
 * The generic record rail (pursuit, RFI response, proposal): back link,
 * identity, ungrouped Home/Chat, collapsible labelled groups, right-aligned
 * counts. Data-free: each record's sidebar passes its groups and counts.
 * Shares the app rail's collapse state (layout store) and ⌘\ shortcut.
 */ export function RecordSidebar({ autoCollapsed = false, back, basePath, counts, groups, name, navigationLabel, subtitle }) {
    const location = useLocation();
    const { user } = useAuth();
    const desktopCollapsed = useLayoutStore((s)=>s.sidebarCollapsed);
    const toggleCollapsed = useLayoutStore((s)=>s.toggleSidebarCollapsed);
    const isNarrow = useMediaQuery('(max-width: 767px)');
    const [autoOverridden, setAutoOverridden] = useState(false);
    useEffect(()=>{
        if (!autoCollapsed) setAutoOverridden(false);
    }, [
        autoCollapsed
    ]);
    const auto = autoCollapsed && !autoOverridden;
    const collapsed = isNarrow || desktopCollapsed || auto;
    // While auto-collapsed, a toggle (button or ⌘\) expands the rail by overriding
    // the page's request; it never flips the hidden persisted value.
    const toggle = useCallback(()=>auto ? setAutoOverridden(true) : toggleCollapsed(), [
        auto,
        toggleCollapsed
    ]);
    // ⌘\ / Ctrl+\ toggles the rail (LAYOUT-SPEC §7), matching the app rail.
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === '\\' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggle();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        toggle
    ]);
    const [groupState, setGroupState] = useState({});
    const toItem = useCallback((def)=>{
        const count = counts?.[def.countKey ?? ''] ?? 0;
        return {
            id: def.id,
            label: def.label,
            icon: def.icon,
            href: recordNavHref(basePath, def),
            onClick: def.onClick,
            active: isRecordNavActive(location.pathname, basePath, def),
            testId: def.testId ?? Testids.recordNav.item,
            count: count > 0 ? count : undefined,
            countLabel: def.countLabel
        };
    }, [
        basePath,
        counts,
        location.pathname
    ]);
    const navGroups = useMemo(()=>groups.map((g)=>({
                id: g.id,
                label: g.label,
                items: g.items.map(toItem)
            })), [
        groups,
        toItem
    ]);
    return /*#__PURE__*/ _jsx(SpecraProgramSidebar, {
        backAction: /*#__PURE__*/ _jsx(SpecraSidebarBackLink, {
            collapsed: collapsed,
            href: back.href,
            label: back.label,
            renderLink: renderRouterLink,
            testId: Testids.recordNav.back
        }),
        className: "h-full max-md:max-h-none max-md:w-[var(--component-sidebar-collapsed-width)]",
        collapsed: collapsed,
        collapseToggleTestId: Testids.recordNav.collapse,
        "data-testid": Testids.recordNav.sidebar,
        footer: user ? /*#__PURE__*/ _jsx(SidebarUserMenu, {
            collapsed: collapsed,
            user: user
        }) : undefined,
        getGroupProps: (group)=>{
            if (!group.label) return {};
            const containsActive = group.items.some((i)=>i.active);
            const def = groups.find((g)=>g.id === group.id);
            const open = groupState[group.id] ?? ((def?.defaultOpen ?? true) || containsActive);
            return {
                collapsible: true,
                containsActive,
                labelTestId: Testids.recordNav.groupLabel,
                onToggle: ()=>setGroupState((s)=>({
                            ...s,
                            [group.id]: !open
                        })),
                open
            };
        },
        groups: navGroups,
        navigationLabel: navigationLabel,
        onCollapseToggle: isNarrow ? undefined : toggle,
        programId: subtitle,
        programName: name,
        renderLink: renderRouterLink
    });
}
